import { useId, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MailOpen } from 'lucide-react'
import { Button, Card, toast } from '@/components/play'
import { acceptFailure, invitesApi, readAcceptedInvitation } from '@/lib/invites/api'
import type { AcceptFailure, MyInvitation } from '@/lib/invites/types'
import { useAuth } from '@/state/AuthContext'

const formatStart = (startsAt: string | null) =>
  startsAt ? new Date(startsAt).toLocaleDateString(undefined, { dateStyle: 'medium' }) : null

/**
 * Manager invitations waiting for this account (docs/features/event-managers.md), above its events: those to its
 * confirmed address and those to the account by its @username, each with the event, who invites you, and Accept, which
 * loads the account as it is now and opens the event's workspace. One that went
 * meanwhile (revoked, expired, taken) leaves the list; one this account took already opens its workspace.
 */
export function PendingInvitations({
  invitations,
  onGone,
  heading = true,
}: {
  invitations: MyInvitation[]
  /** The invitation can't be accepted any more: take it off the list. */
  onGone: (invitationId: string) => void
  /** Shows the Invites heading; leave it out where the page's own heading already says so. */
  heading?: boolean
}) {
  const { refreshUser } = useAuth()
  const navigate = useNavigate()
  const headingId = useId()
  const [accepting, setAccepting] = useState<string | null>(null)

  if (!invitations.length) return null

  async function accept(invitation: MyInvitation) {
    if (accepting) return
    setAccepting(invitation.id)
    let failure: AcceptFailure
    try {
      const accepted = readAcceptedInvitation(await invitesApi.acceptById(invitation.id))
      if (accepted) {
        await refreshUser()
        navigate(accepted.path)
        return
      }
      failure = { kind: 'failed', offline: false }
    } catch (error) {
      failure = acceptFailure(error)
    }
    setAccepting(null)
    const { event, inviter } = invitation
    switch (failure.kind) {
      case 'already_manager':
        await refreshUser()
        navigate(failure.path ?? `/events/${encodeURIComponent(event.id)}`)
        return
      case 'gone':
      case 'not_found':
        onGone(invitation.id)
        toast({
          title: 'That invite is no longer available',
          description: `Ask ${inviter.organization_name ?? inviter.name ?? 'the organizer'} for a new one.`,
        })
        return
      case 'wrong_account': {
        const hint = failure.handleHint ?? failure.emailHint
        toast({ title: 'This invite is for another account', description: hint ? `Sign in as ${hint}.` : undefined, tone: 'error' })
        return
      }
      default:
        toast({
          title: 'Invite not accepted',
          description: failure.kind === 'failed' && failure.offline ? 'Check your connection and try again.' : 'Try again.',
          tone: 'error',
        })
    }
  }

  return (
    <section className="play-pending-invites" aria-labelledby={heading ? headingId : undefined} aria-label={heading ? undefined : 'Invites'}>
      {heading && <h2 id={headingId} className="play-pending-invites__heading">Invites</h2>}
      <div className="play-event-list">
        {invitations.map((invitation) => {
          const { event, inviter } = invitation
          const from = inviter.organization_name ?? inviter.name
          const date = formatStart(event.starts_at)
          const line = from ? `${from} invites you to help run it` : 'You’re invited to help run it'
          return (
            <Card
              key={invitation.id}
              as="article"
              headingLevel={3}
              icon={<MailOpen />}
              title={event.name}
              description={[line, date].filter(Boolean).join(' · ')}
              actions={
                <Button
                  aria-label={`Accept invite to ${event.name}`}
                  loading={accepting === invitation.id}
                  disabled={!!accepting && accepting !== invitation.id}
                  onClick={() => void accept(invitation)}
                >
                  Accept
                </Button>
              }
            />
          )
        })}
      </div>
    </section>
  )
}
